import { appConfig } from '@/config/context';

export enum DjiSetName {
  Guangzhou = 'dji.chn.gz',
  SiliconValley = 'dji.usa.sv',
  Frankfurt = 'dji.de.fra',
  Singapore = 'dji.sg.sg',
}

export interface DjiSetNameOption {
  label: string;
  setName: DjiSetName;
  /** 切换到该地域后跳转的地址；不配置则保持在当前页面刷新 */
  jumpToAfterSwitch?: string;
}

export const SET_NAME_STORAGE_KEY = 'bugly-dji-set-name';

/** 正式环境默认隐藏地域切换入口，需手动在 localStorage 设置 show-set-name-switch=true 才展示 */
export const SET_NAME_SWITCH_VISIBLE_STORAGE_KEY = 'show-set-name-switch';

export const DEFAULT_DJI_SET_NAME = DjiSetName.Guangzhou;

export const DJI_SET_NAME_OPTIONS: DjiSetNameOption[] = [
  { label: '广州', setName: DjiSetName.Guangzhou },
  { label: '硅谷', setName: DjiSetName.SiliconValley },
  { label: '法兰克福', setName: DjiSetName.Frankfurt },
  { label: '新加坡', setName: DjiSetName.Singapore },
];

export const getDjiSetNameOption = (setName?: string | null) => (
  DJI_SET_NAME_OPTIONS.find(item => item.setName === setName)
);

const TRPC_REQUEST_PATH_PREFIX = '/v5/trpc';

export const isSetNameSwitchEnabled = () => Boolean(appConfig.enableSetNameSwitch);

export const isSetNameSwitchVisible = () => {
  if (!isSetNameSwitchEnabled() || typeof window === 'undefined') {
    return false;
  }

  return window.localStorage.getItem(SET_NAME_SWITCH_VISIBLE_STORAGE_KEY) === 'true';
};

export const isValidDjiSetName = (value?: string | null): value is DjiSetName => (
  Boolean(getDjiSetNameOption(value))
);

export const getCurrentDjiSetName = (): DjiSetName | undefined => {
  if (!isSetNameSwitchVisible()) {
    return undefined;
  }

  const savedSetName = window.localStorage.getItem(SET_NAME_STORAGE_KEY);
  return isValidDjiSetName(savedSetName) ? savedSetName : DEFAULT_DJI_SET_NAME;
};

export const setCurrentDjiSetName = (setName: DjiSetName) => {
  if (!isSetNameSwitchEnabled() || typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(SET_NAME_STORAGE_KEY, setName);
};

const splitPathAndSuffix = (value: string) => {
  const match = value.match(/^([^?#]*)(.*)$/);
  return {
    pathname: match?.[1] ?? value,
    suffix: match?.[2] ?? '',
  };
};

const removeKnownSetNamePrefix = (pathname: string) => {
  const normalizedPathname = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const matchedSetName = DJI_SET_NAME_OPTIONS.find(item => normalizedPathname.startsWith(`/${item.setName}${TRPC_REQUEST_PATH_PREFIX}`));
  return matchedSetName ? normalizedPathname.replace(`/${matchedSetName.setName}`, '') : normalizedPathname;
};

const withDjiSetNamePathPrefix = (pathname: string, setName: DjiSetName) => {
  const plainPathname = removeKnownSetNamePrefix(pathname);

  if (!plainPathname.startsWith(TRPC_REQUEST_PATH_PREFIX)) {
    return pathname;
  }

  return `/${setName}${plainPathname}`;
};

export const withDjiSetNameTrpcPrefix = (value?: string) => {
  const setName = getCurrentDjiSetName();

  if (!setName || !value) {
    return value;
  }

  if (/^https?:\/\//.test(value)) {
    const url = new URL(value);
    url.pathname = withDjiSetNamePathPrefix(url.pathname, setName);
    return url.toString();
  }

  const { pathname, suffix } = splitPathAndSuffix(value);
  return `${withDjiSetNamePathPrefix(pathname, setName)}${suffix}`;
};

export const isDjiSetNameTrpcRequest = (value?: string) => {
  if (!value) {
    return false;
  }

  const pathname = /^https?:\/\//.test(value) ? new URL(value).pathname : splitPathAndSuffix(value).pathname;
  const plainPathname = removeKnownSetNamePrefix(pathname);
  return plainPathname.startsWith(TRPC_REQUEST_PATH_PREFIX);
};
